import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';

// Create a Loading component
const Loading = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full"></div>
  </div>
);

// Lazy load components
const Profile = lazy(() => import('@/pages/Profile'));
const SignIn = lazy(() => import('@/pages/SignIn'));
const SignUp = lazy(() => import('@/pages/SignUp'));
const InvitationSignup = lazy(() => import('@/pages/InvitationSignup'));
const InvitedUserSignup = lazy(() => import('@/pages/InvitedUserSignup'));
const Contacts = lazy(() => import('@/pages/Contacts'));
const ContactApproval = lazy(() => import('@/pages/ContactApproval'));
const ContactActionConfirmation = lazy(() => import('@/pages/ContactActionConfirmation'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Admin = lazy(() => import('@/pages/Admin'));
const Switches = lazy(() => import('@/pages/Switches'));
const SwitchDetail = lazy(() => import('@/pages/SwitchDetail'));
const EditSwitch = lazy(() => import('@/pages/EditSwitch'));
const NewSwitch = lazy(() => import('@/pages/NewSwitch'));
const TestSwitch = lazy(() => import('@/pages/TestSwitch'));
const Goodbye = lazy(() => import('@/pages/Goodbye'));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'));
const CheckEmail = lazy(() => import('@/pages/CheckEmail'));
const CreatePin = lazy(() => import('@/pages/CreatePin'));
const VerifyPin = lazy(() => import('@/pages/VerifyPin'));
const ChangePin = lazy(() => import('@/pages/ChangePin'));
const EmailChange = lazy(() => import('@/pages/EmailChange'));
const Index = lazy(() => import('@/pages/Index'));
const LearnMore = lazy(() => import('@/pages/LearnMore'));
const Faq = lazy(() => import('@/pages/Faq'));
const Legal = lazy(() => import('@/pages/Legal'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Upgrade = lazy(() => import('@/pages/Upgrade'));
const Delivery = lazy(() => import('@/pages/Delivery'));

const AppRoutes = () => {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Landing and public information pages */}
        <Route path="/" element={<Index />} />
        <Route path="/learn-more" element={<LearnMore />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/check-email" element={<CheckEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        
        {/* Important: Make sure invitation routes are accessible without auth */}
        <Route path="/invitation/:token" element={<InvitationSignup />} />
        <Route path="/invited-user-signup/:token" element={<InvitedUserSignup />} />
        <Route path="/contact-approval/:token" element={<ContactApproval />} />
        <Route path="/contact-action-confirmation" element={<ContactActionConfirmation />} />
        <Route path="/d/:token" element={<Delivery />} />
        
        {/* Protected routes using ProtectedRoute component */}
        <Route 
          path="/contacts" 
          element={<ProtectedRoute><Contacts /></ProtectedRoute>} 
        />
        <Route 
          path="/profile" 
          element={<ProtectedRoute><Profile /></ProtectedRoute>} 
        />
        <Route 
          path="/dashboard" 
          element={<ProtectedRoute><Dashboard /></ProtectedRoute>} 
        />
        
        {/* Email change route */}
        <Route 
          path="/email-change" 
          element={<ProtectedRoute><EmailChange /></ProtectedRoute>} 
        />
        
        {/* Upgrade route */}
        <Route 
          path="/upgrade" 
          element={<ProtectedRoute><Upgrade /></ProtectedRoute>} 
        />
        
        {/* Admin route — placeholder for future user management */}
        <Route 
          path="/admin" 
          element={<ProtectedRoute requireAdmin={true}><Admin /></ProtectedRoute>} 
        />
        <Route 
          path="/admin/*" 
          element={<Navigate to="/admin" replace />} 
        />
        
        {/* Switches routes */}
        <Route 
          path="/switches" 
          element={<ProtectedRoute><Switches /></ProtectedRoute>} 
        />
        <Route 
          path="/switches/:id" 
          element={<ProtectedRoute><SwitchDetail /></ProtectedRoute>} 
        />
        <Route 
          path="/switches/:id/edit" 
          element={<ProtectedRoute><EditSwitch /></ProtectedRoute>} 
        />
        <Route 
          path="/new-switch" 
          element={<ProtectedRoute><NewSwitch /></ProtectedRoute>} 
        />
        <Route 
          path="/test-switch" 
          element={<ProtectedRoute><TestSwitch /></ProtectedRoute>} 
        />
        
        {/* PIN routes */}
        <Route 
          path="/create-pin" 
          element={<ProtectedRoute><CreatePin /></ProtectedRoute>} 
        />
        <Route 
          path="/verify-pin" 
          element={<ProtectedRoute><VerifyPin /></ProtectedRoute>} 
        />
        <Route 
          path="/change-pin" 
          element={<ProtectedRoute><ChangePin /></ProtectedRoute>} 
        />
        
        {/* Goodbye page - accessible without auth */}
        <Route path="/goodbye" element={<Goodbye />} />
        <Route path="/black" element={<Navigate to="/goodbye" replace />} />
        
        {/* 404 Not Found route */}
        <Route path="/not-found" element={<NotFound />} />
        
        {/* Catch-all route for any paths not matched above */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
